<template>
  <div class="layout-main">
    <div class="layout-actions">
      <button class="layout-button" @click="expandAll">
        <v-icon class="material-icons-outlined ml-1 layout-icon">open_in_full</v-icon>
        {{ $t('grid.layouts.sidebar.expandAll') }}
      </button>
      <button class="layout-button" @click="collapseAll">
        <v-icon class="material-icons-outlined ml-1 layout-icon">close_fullscreen</v-icon>
        {{ $t('grid.layouts.sidebar.collapseAll') }}
      </button>
    </div>
    <div class="layout-content">
      <layout-options
        class="mb-2"
        :active-layout-name="activeLayoutName"
        :has-active-layout="hasActiveLayout"
        :can-edit="canEditActiveLayout"
        :is-recommended-layout="isActiveLayoutRecommended"
        @save="saveLayout"
        @update="updateLayout"
      />
      <ag-grid-vue
        style="width: 100%;"
        class="ag-theme-custom ag-theme-custom--layouts flex-grow-1"
        :column-defs="columnDefs"
        :row-data="layoutData"
        :group-display-type="groupDisplayType"
        :group-default-expanded="-1"
        :grid-options="gridOptions"
        @grid-ready="onGridReady"
        @row-clicked="onRowClicked"
      />
    </div>
    <confirm-dialog
      :is-open="isDeleteLayoutDialogOpen"
      :ok-btn-text="$t('actions.delete')"
      :cancel-btn-text="$t('actions.cancel')"
      @onOk="confirmDeleteLayout"
      @onCancel="cancelDeleteLayout"
    >
      <template #body>
        <div class="delete-layout-message">
          {{ $t('grid.layouts.deleteLayout.message', { name: layoutToDeleteName }) }}
        </div>
        <div class="delete-layout-submessage">
          {{ $t('grid.layouts.deleteLayout.submessage') }}
        </div>
      </template>
    </confirm-dialog>
  </div>
</template>

<script>
import { AgGridVue } from 'ag-grid-vue';
import { get, differenceBy, isEmpty } from 'lodash';
import { mapActions, mapGetters, mapMutations, mapState } from 'vuex';
import LayoutOptions from './layout-options.vue';
import AgGridLayoutRow from './ag-grid-layout-row.vue';
import gridLayoutTypes from '@enums/grid-layout-types';

export default {
  name: 'LayoutsComponent',
  components: {
    AgGridVue,
    LayoutOptions,
    // eslint-disable-next-line vue/no-unused-components
    AgGridLayoutRow,
  },
  data() {
    return {
      params: null,
      isDeleteLayoutDialogOpen: false,
      layoutToDelete: null,
      layoutGridApi: null,
      gridOptions: {
        rowSelection: 'single',
        suppressContextMenu: true,
        headerHeight: 0,
      },
      columnDefs: [
        {
          field: 'layoutType',
          rowGroup: true,
          flex: 1,
          minWidth: 100,
          valueFormatter: params => {
            const layoutType = get(params, 'value', gridLayoutTypes.USER);
            if (layoutType === gridLayoutTypes.RECOMMENDED) {
              return this.$t('grid.layouts.sidebar.recommendedLayouts');
            }
            return this.$t('grid.layouts.sidebar.userLayouts');
          },
          cellRenderer: 'AgGridLayoutRow',
          cellRendererParams: () => ({
            onDeleteLayout: this.deleteUserLayout,
            context: { gridType: this.effectiveGridType },
          }),
        },
      ],
      groupDisplayType: 'groupRows',
    };
  },
  computed: {
    ...mapGetters('gridLayouts', ['getLayouts', 'getActiveLayout']),
    ...mapGetters('context', ['isOWUser']),
    ...mapState('context', ['profile']),
    layoutData() {
      return this.getLayouts;
    },
    activeLayout() {
      return this.getActiveLayout(this.effectiveGridType);
    },
    activeLayoutName() {
      return this.activeLayout && this.activeLayout.name;
    },
    hasActiveLayout() {
      return !!this.activeLayout;
    },
    isActiveLayoutRecommended() {
      return get(this.activeLayout, 'layoutType') === gridLayoutTypes.RECOMMENDED;
    },
    canEditActiveLayout() {
      if (!this.activeLayout) return false;
      const isOwner = get(this.activeLayout, 'createdBy._id', '') === get(this.profile, '_id', '');
      const isRecommendedAndOW =
        this.isOWUser && this.activeLayout.layoutType === gridLayoutTypes.RECOMMENDED;
      return isOwner || isRecommendedAndOW;
    },
    effectiveGridType() {
      const gridType = get(this.params, 'context.gridType');
      if (!gridType) {
        console.error('gridType is not set in context');
        return null;
      }
      return get(this.params, 'context.gridType');
    },
    parentGridApi() {
      return get(this.params, 'api', null);
    },
    layoutToDeleteName() {
      return this.layoutToDelete && this.layoutToDelete.name ? this.layoutToDelete.name : '';
    },
  },
  watch: {
    activeLayout: {
      handler(newLayout) {
        if (newLayout && this.layoutGridApi) {
          this.applyColumnLayout(newLayout.columnState);
        }
      },
      immediate: false,
    },
    getLayouts: {
      handler(newLayouts) {
        // Refresh the layouts grid when the Vuex layouts array changes
        if (this.layoutGridApi) {
          this.layoutGridApi.setGridOption('rowData', newLayouts);
        }
      },
      deep: true,
    },
  },

  beforeDestroy() {
    this.clearActiveLayout(this.effectiveGridType);
  },

  methods: {
    ...mapActions('gridLayouts', [
      'createAndSetActiveLayout',
      'deleteAndClearActiveLayout',
      'setActiveLayoutAndSave',
      'setActiveLayout',
      'saveLayoutPreference',
      'clearActiveLayout',
    ]),
    ...mapActions('gridLayouts', { updateLayoutAction: 'updateLayout' }),
    ...mapMutations('notifications', ['addNotification']),

    expandAll() {
      if (this.layoutGridApi) {
        this.layoutGridApi.expandAll();
      }
    },
    collapseAll() {
      if (this.layoutGridApi) {
        this.layoutGridApi.collapseAll();
      }
    },
    async saveLayout({ name, closeModal }) {
      if (!this.parentGridApi) {
        return;
      }

      try {
        const columnState = this.parentGridApi.getColumnState();
        const layoutData = {
          name,
          associatedTable: this.effectiveGridType,
          columnState,
        };
        await this.createAndSetActiveLayout({
          layoutData,
          gridType: this.effectiveGridType,
        });

        this.addNotification({
          id: `layout-saved-${Date.now()}`,
          jobStatus: 'finished',
          baseTranslation: 'grid.layouts.messages.saveSuccess',
        });

        closeModal();
      } catch (error) {
        console.error('Failed to save layout:', error);
        this.addNotification({
          id: `layout-save-error-${Date.now()}`,
          jobStatus: 'failed',
          baseTranslation: 'grid.layouts.messages.saveError',
        });
      }
    },
    async updateLayout() {
      if (!this.parentGridApi || !this.activeLayout) {
        return;
      }

      try {
        const columnState = this.parentGridApi.getColumnState();
        await this.updateLayoutAction({
          layoutId: this.activeLayout._id,
          layoutData: {
            columnState,
          },
          gridType: this.effectiveGridType,
        });

        this.addNotification({
          id: `layout-updated-${Date.now()}`,
          jobStatus: 'finished',
          baseTranslation: 'grid.layouts.messages.updateSuccess',
        });
      } catch (error) {
        console.error('Failed to update layout:', error);
        this.addNotification({
          id: `layout-update-error-${Date.now()}`,
          jobStatus: 'failed',
          baseTranslation: 'grid.layouts.messages.updateError',
        });
      }
    },
    async selectLayout(layout) {
      try {
        const isAlreadyActive = this.activeLayout && this.activeLayout._id === layout._id;
        // Always set the active layout (this will trigger the watcher to apply the column layout)
        await this.setActiveLayout({
          layout,
          gridType: this.effectiveGridType,
        });

        // Only save as preference if it's not already the active layout
        if (!isAlreadyActive) {
          await this.saveLayoutPreference({
            layout,
            gridType: this.effectiveGridType,
          });
        }
      } catch (error) {
        console.error('Failed to apply layout or save preference:', error);
      }
    },
    async deleteUserLayout(layout) {
      this.layoutToDelete = layout;
      this.isDeleteLayoutDialogOpen = true;
    },
    async confirmDeleteLayout() {
      if (!this.layoutToDelete) {
        return;
      }

      try {
        await this.deleteAndClearActiveLayout({
          layoutId: this.layoutToDelete._id,
          gridType: this.effectiveGridType,
        });
      } catch (error) {
        console.error('Failed to delete layout:', error);
      } finally {
        this.cancelDeleteLayout();
      }
    },
    cancelDeleteLayout() {
      this.isDeleteLayoutDialogOpen = false;
      this.layoutToDelete = null;
    },
    async onGridReady(params) {
      this.layoutGridApi = params.api;
    },
    onRowClicked(event) {
      // Ignore clicks on group rows (expand/collapse)
      if (event.node.group) {
        return;
      }

      this.selectLayout(event.data);
    },
    applyColumnLayout(layoutColumnsState) {
      const currentColumnsState = this.parentGridApi.getColumnState();

      // hide columns that are currently visible but not presented in the layout to apply
      const columnsToHide = differenceBy(currentColumnsState, layoutColumnsState, 'colId');
      columnsToHide.forEach(column => {
        column.hide = true;
      });

      this.parentGridApi.applyColumnState({
        state: [...layoutColumnsState, ...columnsToHide],
        applyOrder: true,
      });

      // Call the callback function if provided in toolPanelParams
      const onLayoutApplied = get(this.params, 'onLayoutApplied');
      if (onLayoutApplied && typeof onLayoutApplied === 'function') {
        onLayoutApplied({
          columnState: this.parentGridApi.getColumnState(),
          previousColumnState: currentColumnsState,
          layout: this.activeLayout,
        });
      }

      // Show success notification using Vuex notifications module
      this.addNotification({
        id: `layout-applied-${Date.now()}`,
        jobStatus: 'success',
        baseTranslation: 'grid.layouts.messages.applySuccess',
      });

      // get columns in layout that are missing in the current columnsConfig
      const missingColumns = differenceBy(layoutColumnsState, currentColumnsState, 'colId');
      if (!isEmpty(missingColumns)) {
        console.warn(
          `Missing columns: ${JSON.stringify(missingColumns)} from layout ${this.activeLayoutName}`
        );
      }
    },
  },
};
</script>

<style lang="scss" scoped>
@import '@style/base/_variables';

.layout-main {
  padding: 10px;
  width: 100%;
  height: 100%;
  display: flex;
  flex-direction: column;
  overflow: hidden;

  .layout-actions {
    display: flex;
    gap: 10px;
    margin-bottom: 10px;
    flex-shrink: 0;
  }

  .layout-content {
    display: flex;
    flex-direction: column;
    flex: 1;
    min-height: 0;
    overflow: hidden;
  }

  // Prevent Vuetify dialog wrapper (layout row) from taking flex space
  > .layout {
    flex: none;
  }

  .layout-button {
    text-decoration: underline;
    color: $pricing-primary;
  }

  .layout-icon {
    color: $pricing-primary;
    font-size: 12px;
  }
}

.ag-theme-custom.ag-theme-custom--layouts {
  --ag-borders: none;
  --ag-border-color: transparent;
}

::v-deep {
  .ag-theme-custom.ag-theme-custom--layouts {
    // AG Grid header may still show a 1px line even with minHeight: 0
    .ag-header {
      height: 0 !important;
      min-height: 0 !important;
      border-bottom: none !important;
    }
    .ag-aria-description-container {
      display: none;
    }
  }
}
.error-list {
  color: #d32f2f;
  margin-top: 6px;

  .error {
    font-size: 12px;
  }
}

.delete-layout-message {
  margin-bottom: 8px;
  font-weight: 500;
}

.delete-layout-submessage {
  color: var(--v-text-secondary);
  font-size: 0.95rem;
}
</style>
